"use strict";
/**
 * Reportan Excel editor mutations shared by Reportan Web and Reportan Mobile.
 * Both clients must send the same `mutateScheduleGrid` sequence for the same edit;
 * the callable is the only writer of `scheduleVisits` and `scheduleGridRows`.
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.getReportanExcelTextNote = getReportanExcelTextNote;
exports.createReportanExcelDraft = createReportanExcelDraft;
exports.switchReportanExcelDraftMode = switchReportanExcelDraftMode;
exports.planReportanExcelCellColor = planReportanExcelCellColor;
exports.planReportanExcelSave = planReportanExcelSave;
exports.planReportanExcelRemove = planReportanExcelRemove;
const reportan_excel_grid_1 = require("./reportan-excel-grid");
/** A whole-row note belongs to the last comment cell of its third line. */
function getReportanExcelTextNote(target) {
    return target.cellNote || (target.lineIndex === 2 && target.columnIndex === 6 ? target.rowNote : undefined);
}
function colorForMode(target, mode) {
    return mode === 'text' ? getReportanExcelTextNote(target)?.color || '' : target.color || '';
}
function createReportanExcelDraft(target) {
    const textNote = getReportanExcelTextNote(target);
    const mode = textNote?.text ? 'text' : 'visit';
    const visit = target.visit;
    return {
        mode,
        text: textNote?.text || '',
        color: colorForMode(target, mode),
        cellColor: target.cellNote?.color || '',
        fields: {
            time: visit?.time || target.defaultTime,
            name: visit?.name || '',
            // Older Excel editors stored a timezone placeholder in this identity field.
            tz: visit?.tz === 'Asia/Jerusalem' ? '' : visit?.tz || '',
            phone: visit?.phone || '',
            address: visit?.address || '',
            email: visit?.email || '',
            price: visit?.price || '',
            payment: visit?.payment || '',
            receipt: visit?.receipt || '',
            comment: visit ? (0, reportan_excel_grid_1.stripLegacyScheduleComment)(visit) : '',
            generalComment: visit?.generalComment || '',
            closer: visit?.closer || '',
            emphasizedContactFields: [...new Set(visit?.emphasizedContactFields ?? [])]
                .filter((field) => field === 'phone' || field === 'address'),
            inspectionType: visit?.inspectionType || 'לא נבחר',
        },
    };
}
function switchReportanExcelDraftMode(target, draft, mode) {
    return { ...draft, mode, color: colorForMode(target, mode) };
}
function cellNotePosition(target) {
    return {
        date: target.date,
        engineerId: target.engineer.userId,
        engineerIndex: target.engineer.index,
        slotIndex: target.slotIndex,
        lineIndex: target.lineIndex,
        columnIndex: target.columnIndex,
    };
}
/** Plans a color-only edit for one physical Sheet cell without touching its visit. */
function planReportanExcelCellColor(target, color, createOperationId) {
    if (color) {
        return {
            version: 2,
            operationId: createOperationId(),
            type: 'upsert_cell_note',
            ...(target.cellNote ? { noteId: target.cellNote.id } : {}),
            note: { ...cellNotePosition(target), text: target.cellNote?.text || '', color },
        };
    }
    if (target.cellNote?.text) {
        return {
            version: 2,
            operationId: createOperationId(),
            type: 'upsert_cell_note',
            noteId: target.cellNote.id,
            note: { ...cellNotePosition(target), text: target.cellNote.text, color: null },
        };
    }
    if (target.cellNote) {
        return { version: 1, operationId: createOperationId(), type: 'delete_note', noteId: target.cellNote.id };
    }
    return null;
}
/** Throws a Hebrew, user-facing message when the draft cannot be saved. */
function planReportanExcelSave(target, draft, createOperationId) {
    const { fields: { inspectionType, emphasizedContactFields, ...fields }, cellColor, color } = draft;
    const cellNoteId = target.cellNote ? { noteId: target.cellNote.id } : {};
    if (draft.mode === 'text') {
        const textNote = getReportanExcelTextNote(target);
        if (!draft.text.trim() && !cellColor)
            throw new Error('יש להזין טקסט או לבחור צבע לתא');
        if (textNote && textNote.lineIndex === undefined) {
            return [{
                    version: 1,
                    operationId: createOperationId(),
                    type: 'upsert_note',
                    noteId: textNote.id,
                    note: {
                        date: target.date,
                        engineerId: target.engineer.userId,
                        engineerIndex: target.engineer.index,
                        slotIndex: target.slotIndex,
                        text: draft.text.trim(),
                        color: color || null,
                    },
                }];
        }
        return [{
                version: 2,
                operationId: createOperationId(),
                type: 'upsert_cell_note',
                ...cellNoteId,
                note: { ...cellNotePosition(target), text: draft.text.trim(), color: cellColor || null },
            }];
    }
    const mutations = [];
    const hasVisitContent = Boolean(target.visit || fields.time !== target.defaultTime || fields.name.trim() || fields.phone.trim()
        || fields.tz.trim() || fields.address.trim() || fields.email.trim() || fields.price.trim() || fields.payment.trim()
        || fields.receipt.trim() || fields.comment.trim() || fields.generalComment.trim() || fields.closer.trim()
        || (inspectionType && inspectionType !== 'לא נבחר'));
    if (hasVisitContent) {
        mutations.push({
            version: 1,
            operationId: createOperationId(),
            type: 'upsert_visit',
            ...(target.visit ? { visitId: target.visit.id } : {}),
            visit: {
                date: target.date, engineerId: target.engineer.userId, engineerIndex: target.engineer.index,
                slotIndex: target.slotIndex, ...fields, phone: (0, reportan_excel_grid_1.formatSinglePhoneInput)(fields.phone),
                // Older deployed callables use a strict schema that predates this optional field.
                // Keep legacy visits compatible until emphasis is actually used or was already stored.
                ...((target.visit && Object.hasOwn(target.visit, 'emphasizedContactFields')) || emphasizedContactFields?.length
                    ? { emphasizedContactFields: emphasizedContactFields ?? [] }
                    : {}),
                // Send the explicit unselected value so merge writes can clear a previous concrete type.
                ...(inspectionType ? { inspectionType } : {}),
                tz: fields.tz.trim(), color: color || null,
            },
        });
    }
    const cellColorMutation = planReportanExcelCellColor(target, cellColor, createOperationId);
    if (cellColorMutation)
        mutations.push(cellColorMutation);
    return mutations;
}
/** Returns null when there is nothing to delete; the editor then just closes. */
function planReportanExcelRemove(target, mode, createOperationId) {
    const textNote = getReportanExcelTextNote(target);
    if (mode === 'text' && textNote)
        return { version: 1, operationId: createOperationId(), type: 'delete_note', noteId: textNote.id };
    if (mode === 'visit' && target.visit)
        return { version: 1, operationId: createOperationId(), type: 'delete_visit', visitId: target.visit.id };
    return null;
}
